import {
  ActionIcon,
  Button,
  createStyles,
  Flex,
  getStylesRef,
  Group,
  MediaQuery,
  Paper,
  Progress,
  Skeleton,
  Image,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { useSessionContext, useSupabaseClient } from '@supabase/auth-helpers-react';
import { IconCaretLeft, IconCaretRight, IconChevronDownLeft } from '@tabler/icons';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { StackedCarousel, ResponsiveContainer } from 'react-stacked-center-carousel';
import {AdvancedImage, lazyload, accessibility, responsive, placeholder} from '@cloudinary/react';
import { name as transformationName} from "@cloudinary/url-gen/actions/namedTransformation";
import { FALLBACK_IMAGE, IS_DEV, TIER_IMAGES_CDN } from '../src/Constants';
import { cld, delay, getDeviceID, noDragStyles, shuffleArrayBasedOnUUID } from '../src/Utils';
import { useStore } from '../stores/stores';
import { Database } from '../types/supabase';
import { TierLabel } from './Create/SetupTiersScreen';
import { feedback_types } from './FeedbackDialog/FeedbackDialog';
import { TierData } from './GradientPicker/GradientPicker';
import { TemplateData, TierListData } from './TemplateResults';
import { TableImageData } from './TierListPage';
import { TierItemData } from './TierListPage/TierGrid';
import * as LomoTime from '../LomoTimeTracker';
import { TierItem } from './TierListPage/TierRow';

const useStyles = createStyles((theme) => ({
  tierRow: {
    paddingLeft: theme.spacing.sm,
    transition: 'none',
    flex: 1,
    flexGrow: 1,
    border: '1px solid rgba(0,0,0,0)',

    [theme.fn.smallerThan('sm')]: {
      paddingLeft: 2,
    },
  },

  imageGallery: {
    border: '1px solid rgba(0,0,0,0)',
    transition: 'all 0.2s ease',
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0],
    paddingLeft: theme.spacing.sm,
    ...noDragStyles,
    '&::-webkit-scrollbar': {
      height: 10,
      width: 0,
      display: 'none',
      backgroundColor: 'rgba(0,0,0,0)',
    },
    '&::-webkit-scrollbar-thumb': {
      height: 10,
      width: 0,
      backgroundColor: 'rgba(0,0,0,0)',
    },
    '&::-webkit-scrollbar-track': {
      height: 10,
      width: 0,
      backgroundColor: 'rgba(0,0,0,0)',
    },
    '&:hover::-webkit-scrollbar': {
      height: 9,
      backgroundColor: theme.colors.dark[8],
      display: 'block',
    },
    '&:hover::-webkit-scrollbar-thumb': {
      height: 9,
      backgroundColor: theme.colors.violet[2],
      borderRadius: 3,
    },
  },

  imageGalleryMobile: {
    border: '1px solid rgba(0,0,0,0)',
    transition: 'all 0.2s ease',
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0],
    paddingLeft: theme.spacing.sm,
    ...noDragStyles,
    position: 'fixed',
    bottom: 0,
  },

  tierFlex: {
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0],
    border: `1px solid ${
      theme.colorScheme === 'dark' ? theme.colors.dark[9] : theme.colors.gray[5]
    }`,
    userSelect: 'none',
    flex: 1,
    flexGrow: 1,
  },

  tierRowTraverse: {
    border: `1px solid ${theme.primaryColor} !important`,
  },

  tierItem: {
    overflow: 'hidden',
    aspectRatio: '1',
    backgroundColor: theme.colors.dark[3],
    userSelect: 'none',
    MozUserSelect: 'none',
    borderRadius: theme.radius.md,
    width: '100%',
    height: '100%',
    transition: 'all 0.2s ease',
    border: `1.5px solid ${theme.colors.dark[8]}`,
    '&:hover': {
      border: `1.5px solid ${theme.colors.violet[2]}`,
      boxShadow: `3px 3px 3px rgba(0, 0, 0, 0.2)`,
    },

    [`&:hover .${getStylesRef('cardHeader')}`]: {
      opacity: 0,
    },
    position: 'relative',

    img: {
      borderRadius: theme.radius.md,
    },
  },

  cardHeader: {
    ref: getStylesRef('cardHeader'),
    position: 'absolute',
    top: 0,
    transition: 'all 0.2s ease',
    opacity: 1,
    backgroundColor:
      theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
    backdropFilter: 'blur(2px)',
    borderRadius: theme.radius.md,
  },
}));

export const Card = React.memo(
  ({ data, dataIndex }: { data: TierItemData[]; dataIndex: number }) => {
    const item = data[dataIndex];
    const { classes } = useStyles();


  const image1x = cld.image(item.src).setDeliveryType('fetch').namedTransformation(transformationName("tier-list-image-1x"));
  const image2x = cld.image(item.src).setDeliveryType('fetch').namedTransformation(transformationName("tier-list-image-2x"));
  const image3x = cld.image(item.src).setDeliveryType('fetch').namedTransformation(transformationName("tier-list-image-3x"));
  const image4x = cld.image(item.src).setDeliveryType('fetch').namedTransformation(transformationName("tier-list-image-4x"));

  
    return (
      <Group
        w="100%"
        h="100%"
        style={{
          width: '100%',
          height: '100%',
          userSelect: 'none',
        }}
      >
        <div className={classes.tierItem}>
          <div
            style={{
              userSelect: 'none',
              MozUserSelect: 'none',
              width: '100%',
              height: '100%',
            }}
          >
            <AdvancedImage
            cldImg={image3x}
            srcset={`${image3x.toURL()} 1x, ${image4x.toURL()} 1.5x`}
              sx={{
                figure: {
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                },
              }}
              style={{
                userSelect: 'none',
                MozUserSelect: 'none',
                ...noDragStyles,
                pointerEvents: 'none',
                objectFit: 'contain',
                aspectRatio: '1',
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              placeholder="blur"
              width="100%"
              height="100%"
              src={item.src}
              alt={`${item.name} Tier List Image. Tier List Maker`}
            />
          </div>
          {item.name && (
            <>
              {isMobile ? (
                <Flex
                  justify="flex-start"
                  align="center"
                  w="100%"
                  mih={30}
                  mah="100%"
                  className={classes.cardHeader}
                  sx={{
                    overflow: 'hidden',
                  }}
                  px={2}
                >
                  <Text size={12} lineClamp={5}>
                    {' '}
                    {item.name.replace(/\.[^/.]+$/, '')}{' '}
                  </Text>
                </Flex>
              ) : (
                <Flex
                  justify="flex-start"
                  align="center"
                  w="100%"
                  mih={30}
                  mah="100%"
                  h="15%"
                  px="sm"
                  className={classes.cardHeader}
                  sx={{
                    overflow: 'hidden',
                  }}
                >
                  <Text size="lg" lineClamp={5}>
                    {' '}
                    {item.name.replace(/\.[^/.]+$/, '')}{' '}
                  </Text>
                </Flex>
              )}
            </>
          )}
        </div>
      </Group>
    );
  }
);

export const QuizComponent = observer(
  ({ tierListData, saveData }: { tierListData: TemplateData; saveData: TierListData | null }) => {
    const router = useRouter();
    const [loaded2, setLoaded] = useState(false);

    const [tierItems, setTierItems] = useState<TierItemData[][]>([]);
    const [tierSaveId, setTierSaveId] = useState<number>(-1);
    const [tierPublic, setTierPublic] = useState<boolean>(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [calculatingResults, setCalculatingResults] = useState(false);
    const carouselRef = useRef<StackedCarousel>();
    // eslint-disable-next-line prefer-destructuring
    const images: TableImageData[] = (tierListData.data as any).images;
    const tiersData: TierData[] = (tierListData.data as any).tierData;
    const supabase = useSupabaseClient<Database>();
    const { session, isLoading } = useSessionContext();
    const itemsRef = useRef<TierItemData[][]>([]);
    const theme = useMantineTheme();
    const [carouselTierData, setCarouselTierData] = useState<TierItemData[]>([]);
    const [maxCount, setMaxCount] = useState<number>(1);
    const {
      setCarouselResultIndex,
      setCarouselResultList,
      carouselResultIndex,
      carouselResultList,
      loaded,
      setFeedbackOpen,
    } = useStore(null);
    const [selectedOptionID, setSelectedTier] = useState<string | undefined>();

    const generateCarouselList = () => {
      const realCarouselResultListData: { tier_item: string; selection_id?: string | undefined }[] =
        [];
      tierItems.slice(0, -1).forEach((currentRow, idx) => {
        const currentSelectionID = tiersData[idx].id;
        currentRow.forEach((tierItem) => {
          realCarouselResultListData.push({
            tier_item: tierItem.id,
            selection_id: currentSelectionID,
          });
        });
      });
      return realCarouselResultListData;
    };

    const onCenterSlideDataIndexChange = (newIndex: number) => {
      if (loaded) {
        setCarouselResultIndex(newIndex);
      }
    };

    useEffect(() => {
      const carouselCurrentData = carouselTierData[carouselResultIndex];
      if (carouselCurrentData) {
        const currentTier = carouselResultList.find(
          (result) => result.tier_item === carouselCurrentData.id
        );
        setSelectedTier(currentTier?.selection_id);
      }
    }, [carouselResultIndex, carouselResultList, carouselTierData]);

    const templateUpdateItems = useCallback(
      (items: TierItemData[][]): void => {
        itemsRef.current = items;
        const savedItems = items.map((row) => row.map((item) => item.id));

        try {
          localStorage.setItem(`TierData-${tierListData.id}`, JSON.stringify(savedItems));
        } catch (err) {
          console.log(err);
        }

        if (session?.user.id) {
          (async () => {
            const result = await supabase
              .from('tier_lists_save')
              .upsert({ template_id: tierListData.id, user_id: session?.user.id, data: savedItems })
              .select('save_id')
              .single();
          })();
        }
      },
      [tierListData.id]
    );

    const SelectTier = async (id: string) => {
      if (calculatingResults || !loaded) {
        return;
      }
      setCalculatingResults(true);
      const carouselCurrentData = carouselTierData[carouselResultIndex];

      if (!carouselCurrentData) {
        console.error('carouselCurrentData is undefined');
        return;
      }

      const currentCarouselResultList = [...carouselResultList];
      const currentIndex = currentCarouselResultList.findIndex(
        (tier) => tier.tier_item === carouselCurrentData.id
      );

      if (currentIndex === -1) {
        setCarouselResultList([
          ...currentCarouselResultList,
          { tier_item: carouselCurrentData.id, selection_id: id },
        ]);
        if (currentCarouselResultList.length + 1 === tierItems.flat().length) {
          modals.openConfirmModal({
            title: 'Quiz Complete',
            centered: true,
            children: (
              <Text size="sm">
                Congratulations! You have completed this tier list. View your results now!
              </Text>
            ),
            labels: { confirm: 'View as Tier List', cancel: 'Return' },
            onCancel: () => console.log('Cancel'),
            onConfirm: () => {
              router.push(`/${tierListData.id}/${tierListData.slug}`);
            },
          });
          setCalculatingResults(false);
          return;
        }
      } else {
        currentCarouselResultList[currentIndex].selection_id = id;
        setCarouselResultList(currentCarouselResultList);
      }

      await delay(200);
      carouselRef.current?.goNext();
      setCalculatingResults(false);
    };

    const deselectTier = useCallback(
      (id: string) => {
        if (calculatingResults || !loaded) {
          return;
        }
        const carouselCurrentData = carouselTierData[carouselResultIndex];
        if (!carouselCurrentData) {
          return;
        }
        const newResult = [...itemsRef.current];
        const currentIndex = carouselResultList.findIndex(
          (tier) => tier.tier_item === carouselCurrentData.id
        );
        const resultList = carouselResultList[currentIndex];

        if (!resultList) {
          return;
        }
        const oldRowIndex = newResult.findIndex((row) =>
          row.some((item) => item.id === resultList.tier_item)
        );

        if (oldRowIndex !== -1) {
          const itemToMove = newResult[oldRowIndex].find(
            (item) => item.id === resultList.tier_item
          );
          if (itemToMove) {
            newResult[oldRowIndex] = newResult[oldRowIndex].filter(
              (item) => item.id !== resultList.tier_item
            );
            // Push the item to its new row
            newResult.at(-1)?.push(itemToMove);
            templateUpdateItems(newResult);
          }
        }
        setCarouselResultList(
          carouselResultList.filter(
            (tierItem) =>
              tierItem.selection_id !== id || tierItem.tier_item !== carouselCurrentData.id
          )
        );
      },
      [
        calculatingResults,
        carouselResultIndex,
        carouselResultList,
        carouselTierData,
        loaded,
        setCarouselResultList,
        templateUpdateItems,
      ]
    );

    useEffect(() => {
      if (!loaded) {
        return;
      }
      if (!itemsRef.current.length) {
        itemsRef.current = tierItems;
      }

      setMaxCount(tierItems.flat().length);

      const shuffled = shuffleArrayBasedOnUUID(
        tierItems.flat().sort((a, b) => (a.id > b.id ? -1 : 1)),
        getDeviceID()
      );

      const presetResultList = generateCarouselList();
      setCarouselResultList(presetResultList);
      const tierItemsSet = new Set(presetResultList.map((item) => item.tier_item));

      // Filter items not in the set and then items in the set
      const itemsNotInSet = shuffled.filter((item) => !tierItemsSet.has(item.id));
      const itemsInSet = shuffled.filter((item) => tierItemsSet.has(item.id));
      // Concatenate the two arrays to get the final sorted array
      const sortedShuffled = itemsNotInSet.concat(itemsInSet);
      setCarouselTierData(sortedShuffled);
    }, [tierItems, loaded]);

    const onChange = useCallback(() => {
      const newResult = [...itemsRef.current];
      let rowChanged = false;
      carouselResultList.forEach((resultList) => {
        // If they skipped it before then we don't want to do anything with it
        if (!resultList.selection_id) {
          return;
        }
        // Find the old row index of the item
        const oldRowIndex = newResult.findIndex((row) =>
          row.some((item) => item.id === resultList.tier_item)
        );
        const newRowIdx = tiersData.findIndex((x) => x.id === resultList.selection_id);
        if (oldRowIndex === newRowIdx) {
          return;
        }
        if (oldRowIndex === -1 || newRowIdx === -1) {
          deselectTier(resultList.selection_id);
          return;
        }

        // Remove the item from its old row
        const itemToMove = newResult[oldRowIndex].find((item) => item.id === resultList.tier_item);

        if (itemToMove) {
          newResult[oldRowIndex] = newResult[oldRowIndex].filter(
            (item) => item.id !== resultList.tier_item
          );
          // Push the item to its new row
          newResult[newRowIdx].push(itemToMove);
          rowChanged = true;
        }
      });
      if (rowChanged) {
        templateUpdateItems(newResult);
      }
      // Optionally: Update the state or do something with newResult here
    }, [carouselResultList, deselectTier, templateUpdateItems, tiersData]);

    useEffect(() => {
      onChange();
    }, [carouselResultList, onChange]);

    useEffect(() => {
      // Always do navigations after the first render
      // if (isLoading) {
      //   return;
      // }

      const newItems: TierItemData[][] = [];

      tiersData.forEach(() => {
        newItems.push([]);
      });

      newItems.push(
        images.map((image) => ({
          id: image.id ?? image.path,
          src: `${TIER_IMAGES_CDN}/${image.path}`,
          name: image.name,
        }))
      );

      async function loadOldMap(oldMap: string[][]) {
        const sortedImageIds: string[] = [];
        const cachedItems: TierItemData[][] = oldMap.map(
          (row: string[]) =>
            row
              .map((id) => {
                const match = images.find(
                  (img: TableImageData) =>
                    (img.id === id && !sortedImageIds.includes(img.id)) ||
                    (img.path === id && !sortedImageIds.includes(img.path))
                );
                if (match) {
                  if (sortedImageIds.includes(match.id ?? match.path)) {
                    return null;
                  }
                  sortedImageIds.push(match.id ?? match.path);
                }
                return {
                  id: match?.id ?? match?.path ?? '',
                  src: match?.path ? `${TIER_IMAGES_CDN}/${match.path}` : FALLBACK_IMAGE,
                  name: match?.name ?? '',
                };
              })
              .filter((imgData) => imgData?.id && imgData?.src) as TierItemData[]
        );

        images.forEach((img: TableImageData) => {
          if (!sortedImageIds.includes(img.path) && (!img.id || !sortedImageIds.includes(img.id))) {
            //Add new images to the bottom row
            cachedItems[cachedItems.length - 1].push({
              id: img.path,
              src: `${TIER_IMAGES_CDN}/${img.path}`,
              name: img.name,
            });
          }
        });

        setTierItems(cachedItems);
      }

      async function loadSharedTierData() {
        try {
          let oldMap: string[][] | null = null;
          const response = await supabase
            .from('tier_lists_save')
            .select('data')
            .eq('template_id', tierListData.id)
            .eq('save_id', saveData?.save_id)
            .single();

          if (response && response.data && (response.data.data as any)?.length) {
            oldMap = response.data.data as any;
            console.log('Loaded from DB');
          }

          if (!oldMap) {
            const savedData = localStorage.getItem(`TierData-${tierListData.id}`);
            if (savedData) {
              oldMap = JSON.parse(savedData);
              console.log('Loaded from Storage');
            }
          }

          if (oldMap) {
            loadOldMap(oldMap);
            return;
          }
          setTierItems(newItems);
        } catch (error) {
          setTierItems(newItems);
        } finally {
          setDataLoaded(true);
          if (saveData && saveData.save_id) {
            await supabase.rpc('on_tier_list_view', {
              id: saveData?.save_id,
            });
          }
        }
      }

      async function loadTierData() {
        try {
          let oldMap: string[][] | null = null;

          if (session?.user.id) {
            const response = await supabase
              .from('tier_lists_save')
              .select('data,save_id,public')
              .eq('template_id', tierListData.id)
              .eq('user_id', session?.user.id)
              .single();

            if (response && response.data && (response.data.data as any)?.length) {
              oldMap = response.data.data as any;
              const is_public = response.data.public;
              const { save_id } = response.data;
              setTierPublic(is_public);
              setTierSaveId(save_id);
              console.log('Loaded from DB', is_public, save_id, oldMap);
            }
            // await checkLikes();
          }

          if (!oldMap) {
            const savedData = localStorage.getItem(`TierData-${tierListData.id}`);
            if (savedData && savedData !== '[]') {
              oldMap = JSON.parse(savedData);
              console.log('Loaded from Storage');
            }
          }

          if (oldMap && oldMap.length) {
            loadOldMap(oldMap);
            return;
          }
          setTierItems(newItems);
        } catch (error) {
          setTierItems(newItems);
        } finally {
          setDataLoaded(true);
          if (!IS_DEV) {
            await supabase.rpc('on_template_view', {
              id: tierListData.id,
            });
          } else {
            console.log('Views not logged in dev');
          }
        }
      }

      if (!saveData) {
        loadTierData();
      } else {
        loadSharedTierData();
      }

      setLoaded(true);
      LomoTime.callAfterTimeElapsedInSeconds(30, () => {
        setFeedbackOpen(true, feedback_types.QUIZ);
      });
    }, [isLoading]);

    const url = `https://lomolist.com/quiz/${tierListData.id}/${tierListData.slug}`;
    return (
      <Stack sx={{ justifyContent: 'center', alignItems: 'center' }}>
        {carouselTierData && carouselTierData.length && loaded && (
          <Paper
            shadow="md"
            p="xl"
            mb="xl"
            radius="md"
            w="100%"
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
            }}
          >
            <Stack sx={{ justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
              <MediaQuery smallerThan="md" styles={{ display: 'none' }}>
                <Title lineClamp={1} size={30}>
                  {' '}
                  {tierListData.name}{' '}
                  {tierListData.name.toLowerCase().includes('tier list')
                    ? ''
                    : 'Tier List Template'}
                </Title>
              </MediaQuery>
              <MediaQuery largerThan="md" styles={{ display: 'none' }}>
                <Title lineClamp={1} order={3}>
                  {' '}
                  {tierListData.name}{' '}
                  {tierListData.name.toLowerCase().includes('tier list')
                    ? ''
                    : 'Tier List Template'}
                </Title>
              </MediaQuery>
              <Group style={{ justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
                <Progress
                  value={(carouselResultList.length / maxCount) * 100}
                  label={`${carouselResultList.length} / ${maxCount}`}
                  size="xl"
                  w="100%"
                  radius="xl"
                  h={25}
                />
                <ActionIcon
                  size="15%"
                  onClick={() => {
                    carouselRef.current?.goBack();
                  }}
                  sx={{ flexDirection: 'column' }}
                >
                  <Text size="xl">Back</Text>
                  <IconCaretLeft size="100%" />
                </ActionIcon>
                {carouselTierData && carouselTierData.length && (
                  <Group
                    w="50%"
                    maw={300}
                    mah={300}
                    sx={{
                      borderRadius: 20,
                      overflow: 'hidden',
                      justifyContent: 'center',
                      alignItems: 'center',
                      aspectRatio: '1',
                      display: 'flex',
                    }}
                  >
                    <ResponsiveContainer
                      carouselRef={carouselRef}
                      render={(parentWidth, c_ref) => {
                        // If you want to use a ref to call the method of StackedCarousel, you cannot set the ref directly on the carousel component
                        // This is because ResponsiveContainer will not render the carousel before its parent's width is determined
                        // parentWidth is determined after your parent component mounts. Thus if you set the ref directly it will not work since the carousel is not rendered
                        // Thus you need to pass your ref object to the ResponsiveContainer as the carouselRef prop and in your render function you will receive this ref object
                        const currentVisibleSlide = 1;
                        // if (parentWidth <= 1440) currentVisibleSlide = 3;
                        // if (parentWidth <= 1080) currentVisibleSlide = 1;
                        return (
                          <StackedCarousel
                            ref={c_ref}
                            slideComponent={Card}
                            slideWidth={parentWidth}
                            carouselWidth={parentWidth}
                            height={parentWidth}
                            disableSwipe={!loaded || calculatingResults}
                            data={carouselTierData}
                            currentVisibleSlide={currentVisibleSlide}
                            onActiveSlideChange={onCenterSlideDataIndexChange}
                            maxVisibleSlide={5}
                            useGrabCursor
                          />
                        );
                      }}
                    />
                  </Group>
                )}
                <ActionIcon
                  size="15%"
                  onClick={() => {
                    carouselRef.current?.goNext();
                  }}
                  sx={{ flexDirection: 'column' }}
                >
                  <Text size="xl">Skip</Text>
                  <IconCaretRight size="100%" />
                </ActionIcon>
              </Group>
              {carouselTierData.length && (
                <Group
                  style={{
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {tiersData.map((tier) => {
                    const selectedElement = selectedOptionID === tier.id;
                    const availableElement = !!selectedOptionID;
                    return (
                      <ActionIcon
                        w={isMobile ? 80 : 100}
                        h={isMobile ? 80 : 100}
                        sx={{
                          opacity: selectedElement || !availableElement ? 1 : 0.4,
                          transition: 'all 0.2s ease',
                          color: 'black',
                          '&:hover': {
                            transform: 'scale(1.07)',
                          },
                        }}
                        onClick={() => {
                          selectedElement ? deselectTier(tier.id) : SelectTier(tier.id);
                        }}
                        my={2}
                      >
                        <TierLabel
                          tier={tier}
                          // squareSize="100%"
                          styles={{
                            width: '100%',
                            height: '100%',
                            border: selectedElement ? '3px solid' : '1px solid',
                            borderColor: !selectedElement
                              ? theme.colorScheme === 'dark'
                                ? theme.white
                                : theme.black
                              : theme.colors.green[3],
                          }}
                        />
                      </ActionIcon>
                    );
                  })}
                </Group>
              )}
            </Stack>
          </Paper>
        )}
        <Button
          onClick={() => {
            router.push(`/${tierListData.id}/${tierListData.slug}`);
          }}
          maw={500}
          size="xl"
        >
          View as Tier List
        </Button>
      </Stack>
    );
  }
);
